import { useEffect, useMemo } from 'react';
import type React from 'react';
import { useGraphStore } from '@/store/graphStore';
import { useLayerStore } from '@/store/layerStore';
import type { LayerId } from '@/store/layerStore';
import { useOverlayDataStore } from '@/store/overlayDataStore';
import { AdapterRegistry } from '@/adapters/index';
import type { ThreatOverlayAdapter } from '@/adapters/index';
import type { CytoNodeElement } from '@/types/dns';
import type { InfrastructureGraphHandle } from '@/components/InfrastructureGraph';
import { applyOverlayVisuals, removeOverlayVisuals } from '@/utils/threatVisualization';

/**
 * Orchestrates overlay data fetching and visualization across all threat adapter layers.
 *
 * - When a layer is toggled visible, fetches data and applies visuals via cy ref.
 * - When a scan completes (scanning → false), re-fetches any visible layers.
 * - When a layer is toggled off, clears overlay points and removes visuals.
 * - Cleanup on unmount removes active visuals to prevent animation leaks.
 *
 * Must be called once at the App level. Passes graphRef so visuals are applied
 * imperatively — InfrastructureGraph never re-renders due to overlay changes.
 */
export function useOverlayData(
  graphRef: React.RefObject<InfrastructureGraphHandle | null>,
): void {
  const layers = useLayerStore((s) => s.layers);
  const setLoading = useLayerStore((s) => s.setLoading);
  const setError = useLayerStore((s) => s.setError);
  const elements = useGraphStore((s) => s.elements);
  const scanning = useGraphStore((s) => s.scanning);
  const setPoints = useOverlayDataStore((s) => s.setPoints);
  const clearPoints = useOverlayDataStore((s) => s.clearPoints);

  // Queryable targets: domain and IP nodes only
  const targets = useMemo(
    () =>
      elements
        .filter(
          (el): el is CytoNodeElement =>
            el.group === 'nodes' &&
            (el.data.type === 'domain' || el.data.type === 'ip'),
        )
        .map((el) => el.data.label),
    [elements],
  );

  async function fetchLayer(layerId: LayerId, adapter: ThreatOverlayAdapter): Promise<void> {
    if (targets.length === 0) return;
    setLoading(layerId, true);
    setError(layerId, null);
    try {
      const points = await adapter.fetch(targets);
      setPoints(layerId, points);
      // Apply visuals imperatively — no React re-render in InfrastructureGraph
      const cy = graphRef.current?.getCy();
      if (cy) {
        const config = useLayerStore.getState().layers[layerId];
        applyOverlayVisuals(cy, layerId, points, {
          opacity: config.opacity,
          filterMinScore: config.filterMinScore,
        });
      }
    } catch (err) {
      setError(layerId, err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setLoading(layerId, false);
    }
  }

  // DarkAPI layer
  const darkapiVisible = layers.darkapi.visible;
  useEffect(() => {
    const adapter = AdapterRegistry.get('darkapi');
    if (!adapter) return;

    if (!darkapiVisible) {
      const cy = graphRef.current?.getCy();
      const existing = useOverlayDataStore.getState().points.darkapi ?? [];
      if (cy && existing.length > 0) removeOverlayVisuals(cy, 'darkapi', existing);
      clearPoints('darkapi');
      return;
    }
    if (!scanning) {
      void fetchLayer('darkapi', adapter);
    }

    return () => {
      // Cleanup on unmount while layer is active
      const cy = graphRef.current?.getCy();
      const existing = useOverlayDataStore.getState().points.darkapi ?? [];
      if (cy && existing.length > 0) removeOverlayVisuals(cy, 'darkapi', existing);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkapiVisible, scanning, targets.length]);

}
